import type {
  CreatePropertyProfessionalInsightsParams,
  ProfessionalInsightsReportAddress,
  ProfessionalInsightsReportDate,
} from './interfaces';
import type {
  commonTypes,
  preforeclosureTypes,
  propertiesTypes,
} from '@ltvco/refresh-lib/v1';

const MAX_MARKET_VALUES = 4;
const MAX_ASSESSMENTS = 2;
const MAX_TAX_BILLS = 6;
const MAX_DEEDS = 2;

const slimProfessionalInsightsDate = (
  date?: commonTypes.Date | null
): ProfessionalInsightsReportDate | null => {
  if (!date?.full) {
    return null;
  }
  return {
    full: date.full,
    parsed: date.parsed
      ? {
          year: date.parsed.year ?? undefined,
          month: date.parsed.month ?? undefined,
          day: date.parsed.day ?? undefined,
        }
      : null,
  };
};

const slimProfessionalInsightsAddress = (
  address?: commonTypes.Address | null
): ProfessionalInsightsReportAddress | null => {
  if (!address) {
    return null;
  }
  return {
    full: address.full ?? undefined,
    parsed: address.parsed
      ? {
          primary_address_number:
            address.parsed.primary_address_number ?? undefined,
          street_name: address.parsed.street_name ?? undefined,
          street_type: address.parsed.street_type ?? undefined,
          city: address.parsed.city ?? undefined,
          state: address.parsed.state ?? undefined,
          zip5: address.parsed.zip5 ?? undefined,
        }
      : null,
  };
};

const mostRecentRecordingDate = (
  events: preforeclosureTypes.Preforeclosure['events'] | undefined
): commonTypes.Date | null => {
  if (!events?.length) {
    return null;
  }

  let latest: commonTypes.Date | null = null;
  for (const event of events) {
    const candidate = event.recording_date;
    if (!candidate?.full) {
      continue;
    }
    if (!latest?.full || candidate.full > latest.full) {
      latest = candidate;
    }
  }
  return latest;
};

const normalizedAddressKey = (
  address?: commonTypes.Address | null
): string | null => {
  const parsed = address?.parsed;
  const streetName = parsed?.street_name?.trim().toLowerCase();
  if (!parsed || !streetName) {
    return null;
  }
  return [
    parsed.primary_address_number?.trim().toLowerCase() ?? '',
    streetName,
    parsed.street_type?.trim().toLowerCase() ?? '',
    parsed.zip5?.trim() ?? '',
  ].join('|');
};

const normalizedFullAddress = (
  address?: commonTypes.Address | null
): string | null => {
  const full = address?.full?.trim().toLowerCase();
  return full ? full : null;
};

const isSameAddress = (
  subject?: commonTypes.Address | null,
  candidate?: commonTypes.Address | null
): boolean => {
  const subjectKey = normalizedAddressKey(subject);
  const candidateKey = normalizedAddressKey(candidate);
  if (subjectKey !== null && candidateKey !== null) {
    return subjectKey === candidateKey;
  }

  const subjectFull = normalizedFullAddress(subject);
  const candidateFull = normalizedFullAddress(candidate);
  return (
    subjectFull !== null &&
    candidateFull !== null &&
    subjectFull === candidateFull
  );
};

export type BuildPropertyProfessionalInsightsOptions = {
  preforeclosures?: preforeclosureTypes.Preforeclosures;
};

type PrunedValue =
  | string
  | number
  | boolean
  | PrunedValue[]
  | { [key: string]: PrunedValue };

const isEmptyValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 0);

const isKeptPrunedValue = (
  value: PrunedValue | null | undefined
): value is PrunedValue => !isEmptyValue(value);

const pruneEmptyValues = (value: unknown): PrunedValue | null | undefined => {
  if (Array.isArray(value)) {
    return value.map(pruneEmptyValues).filter(isKeptPrunedValue);
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value)
      .map(([key, val]): [string, PrunedValue | null | undefined] => [
        key,
        pruneEmptyValues(val),
      ])
      .filter((entry): entry is [string, PrunedValue] =>
        isKeptPrunedValue(entry[1])
      );
    return Object.fromEntries(entries);
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  ) {
    return value;
  }

  return undefined;
};

export const buildPropertyProfessionalInsightsData = (
  property: propertiesTypes.Property,
  options: BuildPropertyProfessionalInsightsOptions = {}
): CreatePropertyProfessionalInsightsParams => {
  const owners = (property.owners ?? []).map((owner) => {
    let rawNames: { full?: string | null }[] = [];
    if ('names' in owner && Array.isArray(owner.names)) {
      rawNames = owner.names;
    } else if ('identity' in owner && Array.isArray(owner.identity?.names)) {
      rawNames = owner.identity.names;
    }

    return {
      names: rawNames
        .map((name) => ({
          full: name.full ?? undefined,
        }))
        .filter((name) => Boolean(name.full)),
    };
  });

  const estimatedAvailableEquity =
    property.values?.equities?.[0]?.estimated_available_equity ?? null;

  const recentDeeds = [...(property.deeds ?? [])]
    .sort((a, b) => {
      const aFull = a.transfer?.filing_date?.full ?? '';
      const bFull = b.transfer?.filing_date?.full ?? '';
      return bFull.localeCompare(aFull);
    })
    .slice(0, MAX_DEEDS);

  const params: CreatePropertyProfessionalInsightsParams = {
    parcel_address: slimProfessionalInsightsAddress(property.parcel_address),
    mailing_address: slimProfessionalInsightsAddress(property.mailing_address),
    owners,
    owner_occupied: property.owner_occupied ?? null,
    last_sold_price: property.last_sold_price ?? undefined,
    last_sold_date: slimProfessionalInsightsDate(property.last_sold_date),
    buildings: (property.buildings ?? []).map((building) => ({
      year_built: building.year_built ?? undefined,
    })),
    values: property.values
      ? {
          assessments: [...(property.values.assessments ?? [])]
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
            .slice(0, MAX_ASSESSMENTS)
            .map((assessment) => ({
              year: assessment.year ?? undefined,
              land: assessment.land ?? undefined,
              improvements: assessment.improvements ?? undefined,
            })),
          market: [...(property.values.market ?? [])]
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
            .slice(0, MAX_MARKET_VALUES)
            .map((market) => ({
              year: market.year ?? null,
              total: market.total ?? undefined,
              min: market.min ?? null,
              max: market.max ?? null,
            })),
        }
      : null,
    taxes: property.taxes
      ? {
          bills: [...(property.taxes.bills ?? [])]
            .sort((a, b) => (b.year ?? 0) - (a.year ?? 0))
            .slice(0, MAX_TAX_BILLS)
            .map((bill) => ({
              year: bill.year ?? undefined,
              delinquent_year: bill.delinquent_year ?? null,
            })),
        }
      : null,
    deeds: recentDeeds.map((deed) => ({
      transfer: deed.transfer
        ? {
            transfer_value: deed.transfer.transfer_value ?? undefined,
            filing_date: slimProfessionalInsightsDate(
              deed.transfer.filing_date
            ),
            buyers: (deed.transfer.buyers ?? []).map((buyer) => ({
              name: buyer.name?.full ? { full: buyer.name.full } : null,
            })),
            sellers: (deed.transfer.sellers ?? []).map((seller) => ({
              name: seller.name?.full ? { full: seller.name.full } : null,
            })),
          }
        : null,
    })),
    building_permits: (property.building_permits ?? []).map((permit) => ({
      type: permit.type ?? undefined,
      status: permit.statuses?.[0]?.value ?? undefined,
      publication_date: slimProfessionalInsightsDate(permit.publication_date),
      job_value: permit.job_value ?? undefined,
      classifiers: (permit.classifiers ?? []).map((classifier) => ({
        value: classifier.value ?? undefined,
      })),
    })),
    courts: {
      civils: (property.courts?.civils ?? []).map((civil) => ({
        status: civil.status ?? undefined,
        filing_date: civil.filing_date?.full ?? undefined,
        filings: (civil.filings ?? []).map((filing) => ({
          type: filing.type ?? undefined,
          filing_date: filing.filing_date?.full ?? undefined,
        })),
      })),
    },
    listings: (property.listings ?? []).map((listing) => ({
      status: listing.status ?? undefined,
      initial_list_date: slimProfessionalInsightsDate(
        listing.initial_list_date
      ),
    })),
    preforeclosures: (options.preforeclosures ?? [])
      .filter((item) => isSameAddress(property.parcel_address, item.address))
      .map((item) => ({
        recording_date: slimProfessionalInsightsDate(
          mostRecentRecordingDate(item.events)
        ),
      })),
    estimated_available_equity: estimatedAvailableEquity,
  };

  return pruneEmptyValues(params) as CreatePropertyProfessionalInsightsParams;
};
